import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./styles.module.css";
import feemonkLogo from "../../assets/images/FeeMonk_HighRes_Logo.png"
import slider from "../../assets/images/slider.png"
import axiosInstance from "../../helpers/axios";
import { useNavigate } from 'react-router-dom';
import OtpConsent from "./OtpConsent/OtpConsent";

const MESSAGES = {
  invalid: "This link is invalid or has expired. Please contact your institute/partner for a new link.",
  submitted: "Consent has already been submitted for this application.",
  rateLimited: "Too many attempts, please wait a minute and try again.",
  error: "Something went wrong. Please try again.",
};

const LOCATION_TIMEOUT_MS = 10000;

// Read the link token once, as an opaque string (no decoding), then strip it
// from the address bar so it never lands in history, analytics or Referer.
const takeLinkTokenFromUrl = () => {
  const linkToken = window.location.pathname.substring(1);
  window.history.replaceState(window.history.state, "", "/");
  return linkToken;
};

const getLocation = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      () => resolve(null),
      { timeout: LOCATION_TIMEOUT_MS }
    );
  });

const statusFromError = (error) => {
  switch (error?.response?.status) {
    case 401:
      return "invalid";
    case 409:
      return "submitted";
    case 429:
      return "rateLimited";
    default:
      return "error";
  }
};

function GettingStarted() {
  const navigate = useNavigate();
  const [openOtpConsent, setOpenOtpConsent] = useState(false);
  // Tokens live in memory only: never in storage, cookies, the URL or logs.
  const [linkToken] = useState(takeLinkTokenFromUrl);
  const sessionTokenRef = useRef(null);
  const [summaryDetails, setSummaryDetails] = useState(null);
  const [status, setStatus] = useState("loading");
  const [submitting, setSubmitting] = useState(false);

  const createSession = useCallback(async () => {
    const res = await axiosInstance.post(`/consent-link/session`, { linkToken });
    sessionTokenRef.current = res?.data?.data?.sessionToken;
  }, [linkToken]);

  // Runs an authorised request; on 401 (session expired) gets a fresh session
  // from the same linkToken and retries once. A second 401 means the link is dead.
  const withSession = useCallback(async (request) => {
    const authHeaders = () => ({
      headers: { Authorization: `Bearer ${sessionTokenRef.current}` },
    });
    try {
      return await request(authHeaders());
    } catch (error) {
      if (error?.response?.status !== 401) throw error;
    }
    await createSession();
    return request(authHeaders());
  }, [createSession]);

  const loadSummary = useCallback(async () => {
    setStatus("loading");
    try {
      if (!sessionTokenRef.current) {
        await createSession();
      }
      const res = await withSession((config) =>
        axiosInstance.get(`/consent-link/summary`, config)
      );
      setSummaryDetails(res?.data?.data);
      setStatus("ready");
    } catch (error) {
      setStatus(statusFromError(error));
    }
  }, [createSession, withSession]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleConsent = () => {
    setOpenOtpConsent(!openOtpConsent);
  }

  const handleSubmit = () => {
    if (status !== "ready" || submitting) return;
    handleConsent();
  };

  const submitUserOtpConsent = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const location = await getLocation();
      await withSession((config) =>
        axiosInstance.post(`/consent-link/submit`, location || {}, config)
      );
      navigate("/thank-you");
    } catch (error) {
      setOpenOtpConsent(false);
      setStatus(statusFromError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    if (summaryDetails) {
      setStatus("ready");
    } else {
      loadSummary();
    }
  };

  const renderAction = () => {
    if (status === "loading") {
      return <p className={styles.text}>Loading...</p>;
    }
    if (status === "ready") {
      return (
        <button className={styles.button} onClick={handleSubmit} disabled={submitting}>
          <img src={slider} alt="" />
          <span style={{color:'black'}}>SUBMIT CONSENT</span>
        </button>
      );
    }
    return (
      <div className={styles.message}>
        <p className={styles.text} role="alert">{MESSAGES[status]}</p>
        {status === "error" && (
          <button className={styles.button} onClick={handleRetry}>
            <span style={{color:'black'}}>RETRY</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={styles.main}>
      <img src={feemonkLogo} alt="" className={styles.image} />
      <p className={styles.text}>
      Simplified education fee payments & fiscal management
      </p>
      {status === "ready" && (
        <OtpConsent openOtpConsent={openOtpConsent} submitUserOtpConsent={submitUserOtpConsent} handleConsent={handleConsent} mobile={summaryDetails?.mobile}/>
      )}
      {renderAction()}
    </div>
  );
}

export default GettingStarted;
